import {
    Component,
    Emitter,
    Material,
    Mesh,
    MeshComponent,
    Object3D,
    Texture,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {YogaNodeProps} from '@wonderlandengine/react-ui';
import React, {useContext, useImperativeHandle} from 'react';
import {forwardRef, useEffect, useRef} from 'react';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {Container, MaterialContext} from '@wonderlandengine/react-ui/components';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {Easing, lerp} from '../../utils/lerp.js';

export class SliderComponent extends Component {
    static TypeName = 'slider-component';

    private static readonly SCALE = 0.01;
    private static readonly OFFSET = 400;
    private static readonly ZSCALE = 0.001;

    @property.mesh()
    planeMesh?: Mesh;

    @property.material()
    planeMaterial?: Material;

    @property.float(50)
    marginLeft = 50;

    @property.float(25)
    gap = 25;

    @property.float(200)
    itemWidth = 200;

    onSelectionChanged = new Emitter<[SliderComponent, number]>();
    currentSelectedItem = -1;

    private _items: SliderItem[] = [];
    private _requireUpdate: boolean = false;
    private _diff: number = 0;
    private _targetPos: number = 0;
    private _xPos = 0;
    private _lastPos = {x: 0, y: 0};
    private _isDown = false;
    private _isDragging = false;

    /**
     * Returns whether the slider can scroll to the previous item
     */
    get canScrollPrevious(): boolean {
        return this.currentSelectedItem > 0;
    }

    /**
     * Returns whether the slider can scroll to the next item
     */
    get canScrollNext(): boolean {
        return this.currentSelectedItem < this._items.length - 1;
    }

    init() {}

    start() {
        if (!this.planeMesh) {
            throw new Error('slider-component: planeMesh is required');
        }
        if (!this.planeMaterial) {
            throw new Error('slider-component: planeMaterial is required');
        }
    }

    onActivate(): void {
        this.engine.canvas.onpointermove = this._onPointerMove;
        this.engine.canvas.onpointerup = this._onPointerUp;
    }

    onDeactivate(): void {
        this.engine.canvas.onpointermove = null;
        this.engine.canvas.onpointerup = null;
    }

    private _onPointerMove = (e: PointerEvent) => {
        if (e.type) this.moving(e.clientX, e.clientY);
    };

    private _onPointerUp = () => {
        this.up();
    };

    update(dt: number) {
        // update inertia

        if (!this._isDragging) {
            // move to closest
            this._diff = (this._targetPos - this._xPos) * 0.1;
        }

        this._diff *= 0.95;

        if (Math.abs(this._diff) < 0.01) {
            this._diff = 0;
        }

        this._xPos += this._diff * 2.2;
        this.clampPosition();

        for (let i = 0; i < this.object.children.length; i++) {
            // TK: Normally we should not access children by index,
            // but since they are added dynamically the order is fixed
            const child = this.object.children[i];
            const pos = this._calulatePosition(i);
            const scale = this._calulateScale(i);
            child.setScalingLocal([scale, scale, scale]);
            child.setPositionLocal([pos, 0, scale * SliderComponent.ZSCALE]);
        }

        const closestItem = this.findClosestItem();
        if (this.currentSelectedItem !== closestItem) {
            this.currentSelectedItem = closestItem;
            this._targetPos = closestItem * -(this.itemWidth + this.gap);
            // fire selection changed event
            this.onSelectionChanged.notify(this, closestItem);
        }
    }

    up() {
        this._isDown = false;
        this._isDragging = false;
    }

    down() {
        this._isDown = true;
    }

    setItems(items: SliderItem[]) {
        if (this.object.children.length > 0) {
            for (let index = 0; index < this.object.children.length; index++) {
                const child = this.object.children[index];
                const meshComp = child.getComponent(MeshComponent);
                (meshComp.material as FlatOpaqueTextured).setFlatTexture(
                    items[index].image
                );
                const scale = this._calulateScale(index);
                child.setScalingLocal([scale, scale, scale]);
                child.setPositionLocal([this._calulatePosition(index), 0, scale * 0.01]);
            }
        } else {
            const childObjects = this.engine.scene.addObjects(
                items.length,
                this.object,
                items.length * 2
            );
            for (let index = 0; index < childObjects.length; index++) {
                const obj = childObjects[index];
                const objMaterial = this.planeMaterial.clone() as FlatOpaqueTextured;
                objMaterial.setFlatTexture(items[index].image);

                obj.addComponent(MeshComponent, {
                    mesh: this.planeMesh,
                    material: objMaterial,
                });
                const scale = this._calulateScale(index);
                obj.setScalingLocal([scale, scale, scale]);

                obj.setPositionLocal([this._calulatePosition(index), 0, scale * 0.01]);
            }

            this._items = items;
        }
    }

    /**
     * Handles the movement of the slider.
     *
     * @param {number} x - The x-coordinate of the current position.
     * @param {number} y - The y-coordinate of the current position.
     */
    moving(x: number, y: number) {
        if (this._isDown) {
            if (!this._isDragging) {
                this._isDragging = true;
                this._lastPos = {x, y};
            } else {
                this._diff = x - this._lastPos.x;
                if (this._diff == 0) {
                    return;
                }
                this._requireUpdate = true;
                this._lastPos = {x, y};
            }
        }
    }

    /**
     * Selects the next item in the slider
     * @returns The new selected index
     */
    selectNext(): number {
        if (this.currentSelectedItem < this._items.length - 1) {
            const nextIndex = this.currentSelectedItem + 1;
            this._targetPos = nextIndex * -(this.itemWidth + this.gap);
            return nextIndex;
        }
        return this.currentSelectedItem;
    }

    /**
     * Selects the previous item in the slider
     * @returns The new selected index
     */
    selectPrevious(): number {
        if (this.currentSelectedItem > 0) {
            const prevIndex = this.currentSelectedItem - 1;
            this._targetPos = prevIndex * -(this.itemWidth + this.gap);
            return prevIndex;
        }
        return this.currentSelectedItem;
    }

    /**
     * Sets the currently selected item based on its index.
     *
     * @param {number} index The index of the item to be selected.
     * If the index is out of bounds, a warning is logged to the console.
     */
    setSelectedItem(index: number) {
        if (index >= 0 && index < this._items.length) {
            this.currentSelectedItem = index;
            this._targetPos = index * -(this.itemWidth + this.gap);
        } else {
            console.warn('Invalid index for setSelectedItem:', index);
        }
    }

    private clampPosition() {
        let newPos = this._xPos; // + this.diff;

        if (newPos > 0) {
            newPos = 0;
        }
        const min = -(
            this._items.length * (this.itemWidth + this.gap) -
            this.itemWidth -
            this.itemWidth / 2 +
            this.gap * 2
        );
        if (newPos < min) {
            newPos = min;
        }
        this._xPos = newPos;
    }

    private _calulatePosition(i: number) {
        return (
            (i * (this.itemWidth + this.gap) + this._xPos + SliderComponent.OFFSET) *
            SliderComponent.SCALE
        );
    }

    private _calulateScale(i: number) {
        const scale = Math.max(
            1 -
                Math.abs(i * (this.itemWidth + this.gap) + this._xPos) /
                    SliderComponent.OFFSET,
            0
        );

        return lerp(0, 1, scale, Easing.BumpEasing) * 1.5;
    }

    private findClosestItem() {
        let index = 0;
        for (let i = 0; i < this._items.length; i++) {
            const pos = this._calulatePosition(i);
            if (pos > 2.75) {
                index = i;
                break;
            }
        }
        return index;
    }
}

export type SliderItem = {
    title: string;
    image: Texture;
    id: string | number;
};

// Define the ref type for the Slider component
export interface SliderRef {
    next: () => void;
    previous: () => void;
    getCurrentIndex: () => number;
    setSelectedItem: (index: number) => void;
    canScrollNext: boolean;
    canScrollPrevious: boolean;
}

export const Slider = forwardRef<
    SliderRef,
    YogaNodeProps & {
        items: SliderItem[];
        onSelectionChange?: (index: number) => void;
    }
>((props, ref) => {
    const meshParent = useRef<Object3D>(null);
    const sliderComponent = useRef<SliderComponent>(null);
    const rotbMC = useRotBMaterialContext();
    const mats = useContext(MaterialContext);

    const down = () => {
        if (sliderComponent.current) {
            sliderComponent.current.down();
        }
    };
    // Expose methods through the ref
    useImperativeHandle(ref, () => ({
        next: () => {
            if (sliderComponent.current) {
                const newIndex = sliderComponent.current.selectNext();
                // Call onSelectionChange directly since we're changing programmatically
                if (
                    props.onSelectionChange &&
                    newIndex !== sliderComponent.current.currentSelectedItem
                ) {
                    props.onSelectionChange(newIndex);
                }
            }
        },
        previous: () => {
            if (sliderComponent.current) {
                const newIndex = sliderComponent.current.selectPrevious();
                // Call onSelectionChange directly since we're changing programmatically
                if (
                    props.onSelectionChange &&
                    newIndex !== sliderComponent.current.currentSelectedItem
                ) {
                    props.onSelectionChange(newIndex);
                }
            }
        },
        getCurrentIndex: () => {
            return sliderComponent.current
                ? sliderComponent.current.currentSelectedItem
                : -1;
        },
        get canScrollNext(): boolean {
            return sliderComponent.current ? sliderComponent.current.canScrollNext : false;
        },
        get canScrollPrevious(): boolean {
            return sliderComponent.current
                ? sliderComponent.current.canScrollPrevious
                : false;
        },
        setSelectedItem(index) {
            if (sliderComponent.current) {
                sliderComponent.current.setSelectedItem(index);
            }
        },
    }));
    useEffect(() => {
        const mesh = meshParent.current.findByName('mesh')[0];
        sliderComponent.current =
            mesh.getComponent(SliderComponent) ??
            mesh.addComponent(SliderComponent, {
                planeMesh: rotbMC.planeReference,
                planeMaterial: mats.panelMaterialTextured,
            });
        sliderComponent.current.setItems(props.items);

        const selectionChanged = (slider: SliderComponent, index: number) => {
            if (props.onSelectionChange) {
                props.onSelectionChange(index);
            }
        };

        sliderComponent.current.onSelectionChanged.add(selectionChanged);

        return () => {
            sliderComponent.current.onSelectionChanged.remove(selectionChanged);
        };
    }, [props.items]);

    const meshComponent = React.createElement('mesh', {
        ref: meshParent,
        ...props,
        // set it fixed to 100x100 to lock in the scale. not sure how to otherwise handle this
        width: 100,
        height: 100,
    });
    return (
        <Container onDown={down} {...props}>
            {meshComponent}
        </Container>
    );
});
